/*!
 * Copyright © 2022. Verizon Connect Ireland Limited. All rights reserved.
 */

import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import { map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { EGeofenceDrawingControlShape } from '../../../geofence/models';
import {
  EGeofenceActions,
  GetPlaceDropdownCategories,
  SetGeofenceDrawingControlShape,
  SetGeofenceLatLng,
  SetSelectedGeofenceShape,
  ShowExistingGeofenceForEdit
} from '../../../geofence/store';
import { openManagePostedSpeedPanel } from '../../../manage-posted-speed/store/actions/manage-posted-speed.actions';
import { GoogleMapsShape } from '../../../maps/models';
import { ROUTE_PARAMS } from '../../constants';
import { ESidePanelContent, LatLng, MarkerShape } from '../../models';
import { WindowResizeService } from '../../services';
import { ELayoutActions, OpenEcmPanel, OpenGeofencePanel, OpenSidePanel, OpenTroubleshootingPanel } from '../actions';
import { CloseEcmPanel } from '../actions/ecm-panel.actions';
import { CloseInsightsPanel } from '../actions/insights-panel.actions';
import { CloseSidePanel, ESidePanelActions, UpdateShouldOffsetForSidePanel } from '../actions/side-panel.actions';
import { getIsEcmPanelOpen, getIsSidePanelOpen } from '../selectors';
import { IAppState } from '../state';

@Injectable()
export class SidePanelEffects {
  /**
   * Opens the Device Health Check panel. When the ECM diagnostics panel is currently open (e.g. the user
   * picks "Device health check" from the vehicle's left-side menu while ECM is showing), its slice is reset
   * via `CloseEcmPanel` first so the ECM panel does not stay active underneath the new content. Mirrors the
   * close-before-open behaviour of `openEcmPanel$`.
   */
  public openTroubleshootingPanel$ = createEffect(() =>
    this._actions$.pipe(
      ofType<OpenTroubleshootingPanel>(ELayoutActions.OpenTroubleshootingPanel),
      withLatestFrom(this._store.pipe(select(getIsEcmPanelOpen))),
      switchMap(([, isEcmPanelOpen]) => {
        const actions: Action[] = [new CloseInsightsPanel()];
        if (isEcmPanelOpen) {
          actions.push(new CloseEcmPanel());
        }
        actions.push(new OpenSidePanel(ESidePanelContent.DeviceHealthCheck));
        return actions;
      })
    )
  );

  public openEcmPanel$ = createEffect(() =>
    this._actions$.pipe(
      ofType<OpenEcmPanel>(ELayoutActions.OpenEcmPanel),
      switchMap(() => [new CloseInsightsPanel(), new CloseSidePanel(), new OpenSidePanel(ESidePanelContent.Ecm)])
    )
  );

  public openGeofencePanel$ = createEffect(() =>
    this._actions$.pipe(
      ofType<OpenGeofencePanel>(ELayoutActions.OpenGeofencePanel),
      switchMap(action => [
        new CloseInsightsPanel(),
        new OpenSidePanel(ESidePanelContent.Geofence),
        new SetGeofenceLatLng(action.payload),
        new GetPlaceDropdownCategories()
      ])
    )
  );

  public openManagePostedSpeedPanel$ = createEffect(() =>
    this._actions$.pipe(
      ofType(openManagePostedSpeedPanel),
      switchMap(() => [new CloseInsightsPanel()])
    )
  );

  public showExistingGeofenceForEdit$ = createEffect(() =>
    this._actions$.pipe(
      ofType<ShowExistingGeofenceForEdit>(EGeofenceActions.ShowExistingGeofenceForEdit),
      switchMap(action => {
        const markerShape = {
          center: new LatLng(action.payload.center.Latitude, action.payload.center.Longitude),
          radius: action.payload.radius,
          shapes: action.payload.shapecoords[0]?.Points?.map(coord => new LatLng(coord.Latitude, coord.Longitude)),
          shapeColor: '#0077B4',
          shapeType: action.payload.shapetype
        };
        const googleMarkerShape: GoogleMapsShape = new MarkerShape(markerShape).toGoogleMapShape();
        const options = {
          strokeColor: '#000000',
          editable: true,
          draggable: true
        };
        googleMarkerShape.setOptions(options);
        const selectedShape: EGeofenceDrawingControlShape =
          googleMarkerShape instanceof google.maps.Polygon
            ? EGeofenceDrawingControlShape.Polygon
            : googleMarkerShape instanceof google.maps.Rectangle
            ? EGeofenceDrawingControlShape.Rectangle
            : EGeofenceDrawingControlShape.Circle;
        const actions: Action[] = [new SetSelectedGeofenceShape(googleMarkerShape), new SetGeofenceDrawingControlShape(selectedShape)];
        return actions;
      })
    )
  );

  public onCloseSidePanelCleanGeofenceUrl$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType<CloseSidePanel>(ESidePanelActions.CloseSidePanel),
        tap(() => {
          const currentPath = this._location.path();
          const placePaths = [ROUTE_PARAMS.PLACE.EDIT_ROUTE, ROUTE_PARAMS.PLACE.CREATE_ROUTE];
          if (placePaths.some(path => currentPath.includes(`/${path}`))) {
            this._location.replaceState('/');
          }
        })
      ),
    { dispatch: false }
  );

  /**
   * Keeps `shouldOffsetForSidePanel` in the store in sync with the current side-panel open state
   * and the device breakpoint. The map and overlay controls offset only on desktop.
   */
  public updateShouldOffsetForSidePanel$ = createEffect(() =>
    combineLatest([this._store.pipe(select(getIsSidePanelOpen)), this._windowResizeService.isMobile$]).pipe(
      map(([isSidePanelOpen, isMobile]) => new UpdateShouldOffsetForSidePanel(isSidePanelOpen && !isMobile))
    )
  );

  constructor(
    private readonly _actions$: Actions,
    private readonly _location: Location,
    private readonly _store: Store<IAppState>,
    private readonly _windowResizeService: WindowResizeService
  ) {}
}
